import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FiTrash2 } from "react-icons/fi";
import { Button } from "../../common/Button";
import { success } from "../../common/Toastify";
import { releaseBlock } from "../../api/Scheduling/blocks";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { BLOCK_REASON_CODES } from "./constants";
import { formatRange } from "./time";
import { timeZoneLabel } from "./timeZones";
import BlockedAppointments from "./BlockedAppointments";

export default function CalendarBlockDetailModal({ block, organizationRef, timezoneName = "UTC", canEdit = false, onEdit, onHide, onRemoved, onOpenAppointment, appointmentScope, excludedOrganizationRefs }) {
  const [confirming, setConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const keepButton = useRef(null);
  const removeButton = useRef(null);
  const wasConfirming = useRef(false);
  const canManage = canEdit && !!organizationRef && !!block.blockId && block.status !== "released";
  const reason = BLOCK_REASON_CODES.find((item) => item.value === block.reasonCode)?.name || block.reasonCode?.replaceAll("_", " ") || "Blocked time";

  useEffect(() => {
    if (confirming) keepButton.current?.focus();
    else if (wasConfirming.current) removeButton.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);

  const removeBlock = async () => {
    if (!canManage || !confirming || pending.current) return;
    pending.current = true;
    setRemoving(true);
    setError("");
    try {
      await releaseBlock({ organizationRef, blockId: block.blockId });
    } catch (cause) {
      setError(schedulingErrorMessage(cause, "Could not release this block. Please try again."));
      pending.current = false;
      setRemoving(false);
      return;
    }
    success("Block released");
    onRemoved();
  };

  return (
    <Modal show onHide={() => { if (!pending.current) onHide(); }} centered scrollable backdrop="static" keyboard={!removing}
      className="custom-dialog scheduling-dialog scheduling-block-detail" aria-labelledby="block-detail-title">
      <Modal.Header closeButton={!removing}>
        <Modal.Title id="block-detail-title">{confirming ? "Release this block?" : "Blocked time"}</Modal.Title>
      </Modal.Header>
      <Modal.Body aria-busy={removing}>
        <p className="mb-1">{formatRange(block.startsAt, block.endsAt, timezoneName)}</p>
        <p className="schedule-help">{reason} · {timeZoneLabel(timezoneName)}</p>
        {!confirming && <p className="schedule-help">New bookings are stopped. Existing appointments remain booked until rescheduled or cancelled.</p>}
        {!confirming && canManage && onOpenAppointment && (block.practitionerRef || block.scheduleId) && <BlockedAppointments
          block={block} organizationRef={organizationRef} timezoneName={timezoneName} onOpenAppointment={onOpenAppointment}
          scope={appointmentScope} excludedOrganizationRefs={excludedOrganizationRefs} />}
        {confirming && <p className="scheduling-block-remove-message">This time will no longer be blocked.</p>}
        {error && <p role="alert" className="scheduling-block-remove-error">{error}</p>}
      </Modal.Body>
      {(canManage || confirming) && <Modal.Footer className="schedule-dialog-footer">
        {confirming ? (
          <>
            <Button ref={keepButton} variant="secondary" disabled={removing} onClick={() => { setConfirming(false); setError(""); }}>Keep block</Button>
            <Button className="scheduling-block-remove-confirm" disabled={!canManage} isLoading={removing} onClick={removeBlock}>
              {removing ? "Releasing…" : "Release"}
            </Button>
          </>
        ) : (
          <>
            {canManage && <Button ref={removeButton} variant="secondary" className="scheduling-block-remove" startIcon={<FiTrash2 aria-hidden="true" />} onClick={() => setConfirming(true)}>Release</Button>}
            {canManage && <Button variant="secondary" onClick={() => onEdit(block)}>Edit block</Button>}
          </>
        )}
      </Modal.Footer>}
    </Modal>
  );
}
